// =============================================================================
// Módulo Ocorrências — tipos do PROTÓTIPO (dados mock, sem backend).
// O contrato real será definido no plano de implementação.
// =============================================================================

export type MockProfile =
	| 'ADMIN'
	| 'SSMA_MANAGER'
	| 'MANAGER'
	| 'TECHNICIAN'
	| 'MEDICINE'

export const PROFILE_LABEL: Record<MockProfile, string> = {
	ADMIN: 'Administrador do Sistema',
	SSMA_MANAGER: 'Gestor SSMA',
	MANAGER: 'Gestor',
	TECHNICIAN: 'Técnico',
	MEDICINE: 'Medicina',
}

export type OccurrenceStatus =
	| 'DRAFT'
	| 'REGISTERED'
	| 'AWAITING_CLASSIFICATION'
	| 'INVESTIGATING'
	| 'AWAITING_VALIDATION'
	| 'ACTION_PLAN'
	| 'AWAITING_EFFICACY'
	| 'CLOSED'
	| 'CANCELLED'
	| 'REOPENED'

export const STATUS_LABEL: Record<OccurrenceStatus, string> = {
	DRAFT: 'Rascunho',
	REGISTERED: 'Registrada',
	AWAITING_CLASSIFICATION: 'Aguardando classificação',
	INVESTIGATING: 'Em investigação',
	AWAITING_VALIDATION: 'Aguardando validação',
	ACTION_PLAN: 'Plano de ação em andamento',
	AWAITING_EFFICACY: 'Aguardando avaliação de eficácia',
	CLOSED: 'Encerrada',
	CANCELLED: 'Cancelada',
	REOPENED: 'Reaberta',
}

/** Ordem do ciclo de vida principal (exceções ficam fora). */
export const STATUS_FLOW: OccurrenceStatus[] = [
	'DRAFT',
	'REGISTERED',
	'AWAITING_CLASSIFICATION',
	'INVESTIGATING',
	'AWAITING_VALIDATION',
	'ACTION_PLAN',
	'AWAITING_EFFICACY',
	'CLOSED',
]

export type OccurrenceType =
	| 'WORK_ACCIDENT'
	| 'INCIDENT'
	| 'NEAR_MISS'
	| 'SSMA_DEVIATION'
	| 'RNC'
	| 'GOVERNANCE_DEVIATION'
	| 'OCCUPATIONAL_DISEASE'
	| 'PROPERTY_DAMAGE'
	| 'ENVIRONMENTAL'

export const TYPE_LABEL: Record<OccurrenceType, string> = {
	WORK_ACCIDENT: 'Acidente de trabalho',
	INCIDENT: 'Incidente',
	NEAR_MISS: 'Quase acidente',
	SSMA_DEVIATION: 'Desvio de SSMA',
	RNC: 'RNC',
	GOVERNANCE_DEVIATION: 'Desvio de governança',
	OCCUPATIONAL_DISEASE: 'Doença ocupacional',
	PROPERTY_DAMAGE: 'Dano material',
	ENVIRONMENTAL: 'Ocorrência ambiental',
}

/** Tipos com colaborador envolvido / impacto médico (ativam etapas). */
export const TYPES_WITH_PERSON: OccurrenceType[] = [
	'WORK_ACCIDENT',
	'INCIDENT',
	'NEAR_MISS',
	'OCCUPATIONAL_DISEASE',
]
export const TYPES_WITH_MEDICAL: OccurrenceType[] = [
	'WORK_ACCIDENT',
	'OCCUPATIONAL_DISEASE',
]

export type ClassificationCode =
	| 'FA'
	| 'CAF'
	| 'SAF_RE'
	| 'SAF'
	| 'PS'
	| 'QA'
	| 'TRAJETO_CAF'
	| 'TRAJETO_SAF'
	| 'QA_TRAJETO'
	| 'DOENCA'
	| 'DANO_MATERIAL'
	| 'SITUACAO_INSEGURA'
	| 'NAO_ACIDENTE'

export interface ClassificationInfo {
	code: ClassificationCode
	label: string
	/** Ajuda "O que é?" — conteúdo seria administrável/versionado (IT G 21 005). */
	definition: string
	criteria: string
	examples: string
	notApplicable: string
	guidingQuestions: string[]
}

export type Potential = 'N1' | 'N2' | 'N3' | 'N4' | 'N5'

export type ActionStatus =
	| 'OPEN'
	| 'IN_PROGRESS'
	| 'AWAITING_APPROVAL'
	| 'REJECTED'
	| 'DONE'
	| 'CANCELLED'

export const ACTION_STATUS_LABEL: Record<ActionStatus, string> = {
	OPEN: 'Aberta',
	IN_PROGRESS: 'Em andamento',
	AWAITING_APPROVAL: 'Aguardando aprovação',
	REJECTED: 'Reprovada',
	DONE: 'Concluída',
	CANCELLED: 'Cancelada',
}

export type Efficacy = 'EFFECTIVE' | 'INEFFECTIVE' | null

export interface ActionHistoryEntry {
	at: string
	by: string
	text: string
}

export interface OccurrenceAction {
	id: string
	description: string
	/** Origem obrigatória e rastreável (ocorrência, visita, inspeção...) */
	origin: string
	causeId: string | null
	responsible: string
	approver: string
	dueDate: string
	status: ActionStatus
	efficacy: Efficacy
	efficacyNote?: string
	evidence: string[]
	history: ActionHistoryEntry[]
}

export type Confidentiality = 'PUBLIC' | 'INTERNAL' | 'RESTRICTED'
export const CONFIDENTIALITY_LABEL: Record<Confidentiality, string> = {
	PUBLIC: 'Geral',
	INTERNAL: 'Interno',
	RESTRICTED: 'Restrito',
}

export interface Evidence {
	id: string
	caption: string
	author: string
	date: string
	origin: string
	confidentiality: Confidentiality
	/** Cor do placeholder (protótipo não carrega imagens reais). */
	tone: string
	deletedReason?: string
}

export interface Testimony {
	id: string
	person: string
	role: string
	date: string
	fileName: string
	ocrText: string
	ocrValidatedBy: string | null
}

export interface CommitteeMember {
	id: string
	name: string
	role: string
	area: string
}

export interface TimelineEvent {
	id: string
	/** Data/hora do FATO */
	factAt: string
	/** Data/hora do REGISTRO */
	recordedAt: string
	description: string
	author: string
	origin: 'MANUAL' | 'SYSTEM'
	evidenceRef?: string
}

export interface OccurrenceDocument {
	id: string
	name: string
	category: string
	version: number
	confidentiality: Confidentiality
	retention: string
	author: string
	date: string
	downloads: { by: string; at: string }[]
}

export interface MedicalRecordEntry {
	id: string
	date: string
	subject: string
	note: string
	by: string
}

export interface MedicalData {
	careDate: string
	careTime: string
	doctor: string
	crm: string
	cid: string
	bodySide: string
	externalTreatment: string
	bodyPart: string
	causalLink: string
	catIssued: boolean
	catIssuedAt?: string
	catPdf?: string
	leaveDays: number
	records: MedicalRecordEntry[]
}

export type InvestigationMethod = 'CAUSE_TREE' | 'FIVE_WHYS' | 'ISHIKAWA'
export const METHOD_LABEL: Record<InvestigationMethod, string> = {
	CAUSE_TREE: 'Árvore de Causas',
	FIVE_WHYS: '5 Porquês',
	ISHIKAWA: 'Ishikawa + 5 Porquês',
}

export interface Cause {
	id: string
	text: string
	category?: string
	/** Pai na árvore (null = raiz/evento) */
	parentId: string | null
	/** Para Árvore de Causas: relação com os filhos */
	logic?: 'AND' | 'OR'
	/** Causa-raiz identificada (elegível a ação) */
	isRoot: boolean
	/** Posição livre no canvas da Árvore de Causas (ausente = posição automática) */
	x?: number
	y?: number
}

export interface InvestigationData {
	method: InvestigationMethod | null
	recommended: InvestigationMethod | null
	methodChangeReason?: string
	participants: string[]
	causes: Cause[]
	/** 5 porquês: sequência linear */
	whys: string[]
}

export type MatrixResult =
	| 'ERROR'
	| 'CIRCUMSTANTIAL_VIOLATION'
	| 'DELIBERATE_VIOLATION'
	| 'INCONCLUSIVE'

export const MATRIX_RESULT_LABEL: Record<MatrixResult, string> = {
	ERROR: 'Erro',
	CIRCUMSTANTIAL_VIOLATION: 'Violação circunstancial',
	DELIBERATE_VIOLATION: 'Violação deliberada',
	INCONCLUSIVE: 'Análise inconclusiva',
}

export interface MatrixAnswer {
	questionId: string
	answer: 'YES' | 'NO' | 'NA'
	justification: string
}

export interface MatrixState {
	version: string
	answers: MatrixAnswer[]
	result: MatrixResult | null
	factors: string[]
	validatedBy: string | null
	validatedAt: string | null
	committeeNote: string
}

export interface LessonLearned {
	service: string
	activity: string
	summary: string
	learnings: string
	recommendations: string
	keywords: string[]
	photoIds: string[]
	validated: boolean
}

export interface AuditEntry {
	id: string
	at: string
	by: string
	profile: MockProfile
	event: string
	detail?: string
	/** Valor anterior → novo (quando aplicável) */
	diff?: { field: string; from: string; to: string }
	sensitive?: boolean
}

export interface StatusHistoryEntry {
	at: string
	by: string
	from: OccurrenceStatus | null
	to: OccurrenceStatus
	justification?: string
}

export interface Occurrence {
	id: string
	/** Número sequencial exibido (ex.: 2101) */
	number: number
	type: OccurrenceType
	status: OccurrenceStatus
	date: string
	time: string
	branch: string
	contract: string
	customer: string
	cr: string
	description: string
	fullDescription: string
	potential: Potential
	/** Classificação proposta pelo técnico */
	proposedClassification: ClassificationCode | null
	/** Classificação confirmada pelo Gestor SSMA */
	classification: ClassificationCode | null
	classificationDivergenceReason?: string
	bodyPart: string
	lifeAttitude: string
	equated: boolean
	workRelated: boolean
	/** Local */
	state: string
	city: string
	cep: string
	activityFrequency: string
	workType: string
	serviceType: string
	mainActivity: boolean
	workSchedule: string
	createdBy: string
	createdAt: string
	/** Colaborador */
	employee: {
		name: string
		badge: string
		jobRole: string
		admission: string
		age: number | null
		education: string
		pda: string
	} | null
	evidences: Evidence[]
	testimonies: Testimony[]
	committee: CommitteeMember[]
	timeline: TimelineEvent[]
	investigation: InvestigationData
	matrix: MatrixState
	actions: OccurrenceAction[]
	lesson: LessonLearned | null
	documents: OccurrenceDocument[]
	medical: MedicalData | null
	closure: { decidedBy: string | null; decidedAt: string | null; note: string }
	statusHistory: StatusHistoryEntry[]
	audit: AuditEntry[]
	cancelledReason?: string
}
